import React, { useEffect } from 'react';
import { cn } from '@utils/index';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from 'src/store/auth';

type AuthLayoutProps = {
  children: React.ReactNode | React.ReactNode[];
  title: string;
  description?: React.ReactNode;
  variant?: 'register' | 'login' | 'reset' | 'new-password';
  headerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  contentClassName?: string;
};

const bgMap: Record<NonNullable<AuthLayoutProps['variant']>, string> = {
  register: '/images/register1.png',
  login: '/images/login6.png',
  reset: '/images/reset.png',
  'new-password': '/images/new-password.png',
};

export const AuthLayout = ({
  children,
  title,
  description,
  variant,
  headerClassName,
  titleClassName,
  descriptionClassName,
  contentClassName,
}: AuthLayoutProps) => {
  const bgImage = variant ? bgMap[variant] : undefined;
  const location = useLocation();
  const form = useForm({});
  const clearAuth = useAuthStore((state) => state.clear);

  useEffect(() => {
    form.clearErrors();
    clearAuth();
  }, [location.pathname]);

  return (
    <div className="flex flex-col h-full 2xl:min-h-[calc(100vh-240px)] gap-6 2xl:flex-row justify-center w-full px-0">
      <header
        className={cn(
          'relative rounded-3xl min-h-103 max-h-103 lg:max-h-none max-lg:min-h-83 max-lg:max-h-none max-lg:h-auto max-lg:shrink-0 max-lg:pb-6 max-md:h-auto max-md:shrink-0 max-md:pb-6 bg-primary text-white 2xl:max-w-97 3xl:max-w-121.25 w-full 2xl:min-h-auto h-full',
          headerClassName
        )}
      >
        <section className="left-0 top-0 absolute 2xl:pt-21 px-6 pt-16 max-lg:pt-7 max-lg:relative max-lg:left-auto max-lg:top-auto max-md:relative max-md:left-auto max-md:top-auto">
          <h2 className={cn('text-2xl font-extrabold', titleClassName)}>
            {title}
          </h2>
          {description && (
            <p className={cn('text-base font-light', descriptionClassName)}>
              {description}
            </p>
          )}
        </section>
        <div className="bg-primary absolute top-25 -right-2 w-8 h-8 pointer-events-none select-none hidden 2xl:flex rotate-45" />
      </header>

      <div
        className={cn(
          'px-6 py-10 2xl:pt-21 2xl:pl-23 h-full 2xl:min-h-[calc(100vh-168px)] w-full rounded-2xl transition-all bg-card max-md:pt-5',
          variant && 'bg-right bg-no-repeat',
          contentClassName,
          'auth-image'
        )}
        style={bgImage ? { backgroundImage: `url(${bgImage})` } : {}}
      >
        <div className="rounded-2xl items-center m-auto max-2xl:flex max-2xl:flex-col">
          {children}
        </div>
      </div>
    </div>
  );
};
